import { estado, TTL_MS, CLAVE_PROVEEDORES } from './insurance-catalog-store';
import { getInsuranceProviders, getInsurancePlansByProvider } from './insurance-service-calls';

// ----------------------------------------------------------------------
// Caché de catálogo de seguros (aseguradoras y sus planes).
//
// El catálogo cambia con muy poca frecuencia -se administra en Seguros >
// Aseguradoras- pero se consulta muchísimo: RHFProviderAutocomplete lo pedía
// en un `useEffect(..., [])` SIN caché, así que cada montaje era una petición
// nueva. En el wizard de admisión eso significaba recargar la lista entera
// cada vez que el operador entraba al paso de Seguro, y esa lista es lenta.
//
// Se memoiza la PROMESA, no solo el resultado: dos llamadas simultáneas
// (p. ej. la precarga en segundo plano y el montaje del autocompletado)
// comparten una única petición en vuelo en vez de lanzar dos.
//
// El espejo en sessionStorage existe para sobrevivir a un refresco de página
// dentro de la misma sesión. Es catálogo, no dato clínico: no contiene PII.
//
// El estado mutable y `invalidarCatalogoSeguros` viven en
// insurance-catalog-store.js — ver allí por qué están separados.
// ----------------------------------------------------------------------

export { invalidarCatalogoSeguros } from './insurance-catalog-store';

const leerSesion = (clave) => {
  try {
    const crudo = sessionStorage.getItem(clave);
    if (!crudo) return null;
    const guardado = JSON.parse(crudo);
    if (Date.now() - guardado.cachedAt > TTL_MS) return null;
    return guardado.datos;
  } catch {
    // Una caché ilegible NO puede tumbar la pantalla: se trata como si no
    // existiera y se vuelve a pedir al servidor.
    return null;
  }
};

const escribirSesion = (clave, datos) => {
  try {
    sessionStorage.setItem(clave, JSON.stringify({ datos, cachedAt: Date.now() }));
  } catch {
    // sessionStorage lleno o bloqueado: la caché en memoria sigue sirviendo.
  }
};

/**
 * Aseguradoras, de caché si está vigente.
 *
 * Un fallo NO se memoiza: se limpia la promesa antes de propagar el error,
 * porque memoizar un rechazo dejaría el desplegable vacío durante toda la
 * sesión sin posibilidad de reintento.
 * @returns {Promise<Array>}
 */
export async function getProveedoresCacheados() {
  const enSesion = leerSesion(CLAVE_PROVEEDORES);
  if (enSesion) return enSesion;

  if (!estado.promesaProveedores) {
    estado.promesaProveedores = getInsuranceProviders()
      .then((datos) => {
        const lista = Array.isArray(datos) ? datos : [];
        escribirSesion(CLAVE_PROVEEDORES, lista);
        return lista;
      })
      .catch((error) => {
        estado.promesaProveedores = null;
        throw error;
      });
  }

  return estado.promesaProveedores;
}

/**
 * Planes de una aseguradora, cacheados por proveedor. Mismo criterio que los
 * proveedores: es catálogo, no dato clínico.
 * @param {string} providerId
 * @returns {Promise<Array>}
 */
export async function getPlanesCacheados(providerId) {
  if (!providerId) return [];

  if (!estado.promesasPlanesPorProveedor.has(providerId)) {
    // getInsurancePlansByProvider ya devuelve response.data (el array), no la
    // respuesta de axios: no hay ningún `.data` que desenvolver aquí.
    const promesa = getInsurancePlansByProvider(providerId)
      .then((lista) => (Array.isArray(lista) ? lista : []))
      .catch((error) => {
        estado.promesasPlanesPorProveedor.delete(providerId);
        throw error;
      });
    estado.promesasPlanesPorProveedor.set(providerId, promesa);
  }

  return estado.promesasPlanesPorProveedor.get(providerId);
}

/**
 * Precarga en segundo plano. Fire-and-forget A PROPÓSITO: quien la llama no
 * espera el resultado ni le importa que falle — el objetivo es que la lista ya
 * esté lista cuando el operador llegue a la pantalla de Seguro, y si no llega a
 * tiempo, el autocompletado la pedirá igual (compartiendo esta misma promesa,
 * no lanzando otra).
 *
 * El fallo se traga aquí, y no dentro de `getProveedoresCacheados`, para no
 * dejar una promesa rechazada sin manejar flotando en la consola.
 */
export function precargarCatalogoSeguros() {
  getProveedoresCacheados().catch(() => {});
}
